import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  AcademicCapIcon,
  BriefcaseIcon,
  ChartBarIcon,
  UserGroupIcon,
  BookOpenIcon,
  TrophyIcon,
  PlusIcon,
  EyeIcon,
  CheckCircleIcon,
  UsersIcon,
  DocumentTextIcon,
  StarIcon,
  CogIcon,
  HeartIcon,
  CurrencyDollarIcon,
  SparklesIcon,
  ShieldCheckIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import VerificationsCard from '../components/VerificationsCard';
import SocialConnectionsCard from '../components/SocialConnectionsCard';
import { apiService } from '../services/apiService';

interface DashboardData {
  stats: {
    coursesCompleted: number;
    coursesInProgress: number;
    jobApplications: number;
    certifications: number;
    globalRank: number;
  };
  recentCourses: any[];
  hasActiveCourses: boolean;
}

interface Course {
  id: string;
  title: string;
  category: string;
  industryCategory: string;
  difficulty: string;
  marketData: {
    averageSalary: number;
    jobDemand: number;
    careerPaths: string[];
  };
  skills: string[];
}

interface UserInterests {
  interests: string[];
  careerGoals: string[];
  skillsToLearn: string[];
}

const Dashboard: React.FC = () => {
  const { userProfile, currentUser } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showInterestsModal, setShowInterestsModal] = useState(false);
  const [userInterests, setUserInterests] = useState<UserInterests>({
    interests: [],
    careerGoals: [],
    skillsToLearn: []
  });
  const [recommendedCourses, setRecommendedCourses] = useState<Course[]>([]);
  const [jobSalaryData, setJobSalaryData] = useState<Record<string, number>>({});
  const [careerPaths, setCareerPaths] = useState<any[]>([]);
  const [enrollingPath, setEnrollingPath] = useState<string | null>(null);
  const [setupStatus, setSetupStatus] = useState<{ verified: boolean; socialConnected: boolean } | null>(null);

  const isStudent = userProfile?.userType === 'student';
  const fetchStudentDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch dashboard data');
      
      const result = await response.json();
      if (result.success) {
        setDashboardData(result.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserInterests = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/profiles/interests`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUserInterests(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching user interests:', error);
    }
  };

  const fetchRecommendations = async () => {
    try {
      if ((userInterests?.interests?.length > 0) || (userInterests?.careerGoals?.length > 0)) {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/recommendations`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setRecommendedCourses(result.data.courses || []);
            // Update salary data with career insights
            const insights = result.data.careerInsights || {};
            const salaryData: Record<string, number> = {};
            Object.keys(insights).forEach(career => {
              if (insights[career].averageSalary) {
                salaryData[career] = insights[career].averageSalary;
              }
            });
            setJobSalaryData(salaryData);
          }
        } else if (response.status === 429) {
          console.log('Rate limited, will retry later');
          setTimeout(() => fetchRecommendations(), 3000);
        }
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    }
  };

  const fetchCareerPaths = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/career-paths`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCareerPaths(result.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching career paths:', error);
    }
  };

  const fetchSetupStatus = async () => {
    try {
      const [verifyRes, socialRes] = await Promise.all([
        apiService.get('employment-verification/statuses'),
        apiService.get('social')
      ]);
      const statuses = verifyRes.data.data || {};
      const profiles = socialRes.data.data || [];
      setSetupStatus({
        verified: statuses.employment?.status === 'verified' || statuses.education?.status === 'verified',
        socialConnected: profiles.length > 0
      });
    } catch (error) {
      console.error('Error fetching setup status:', error);
    }
  };

  const enrollInCareerPath = async (pathId: string) => {
    try {
      setEnrollingPath(pathId);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/career-paths/${pathId}/enroll`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success(result.data.message);
          // Refresh dashboard data to show new enrollments
          fetchStudentDashboardData();
        }
      } else {
        throw new Error('Failed to enroll in career path');
      }
    } catch (error) {
      console.error('Error enrolling in career path:', error);
      toast.error('Failed to enroll in career path');
    } finally {
      setEnrollingPath(null);
    }
  };



  useEffect(() => {
    if (isStudent && userProfile?.id && currentUser?.uid) {
      fetchStudentDashboardData();
      fetchUserInterests();
      fetchCareerPaths();
      fetchSetupStatus();
    }
  }, [isStudent, userProfile?.id, currentUser?.uid]);

  useEffect(() => {
    if ((userInterests?.interests?.length > 0) || (userInterests?.careerGoals?.length > 0)) {
      // Debounce the API calls to prevent rapid firing
      const timer = setTimeout(() => {
        fetchRecommendations();
        fetchCareerPaths();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [userInterests, fetchRecommendations, fetchCareerPaths]);

  if (!userProfile || !currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }



  const saveUserInterests = async (interests: UserInterests) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/profiles/interests`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ interests })
      });

      if (response.ok) {
        setUserInterests(interests);
        setShowInterestsModal(false);
        toast.success('Interests saved successfully!');
        // Refresh recommendations and career paths with new interests
        fetchRecommendations();
        fetchCareerPaths();
      } else {
        throw new Error('Failed to save interests');
      }
    } catch (error) {
      console.error('Error saving interests:', error);
      toast.error('Failed to save interests');
    }
  };

  const InterestsModal = () => {
    const [localInterests, setLocalInterests] = useState(userInterests || {
      interests: [],
      careerGoals: [],
      skillsToLearn: []
    });
    const [newInterest, setNewInterest] = useState('');
    const [newCareerGoal, setNewCareerGoal] = useState('');
    const [newSkill, setNewSkill] = useState('');

    const addItem = (type: 'interests' | 'careerGoals' | 'skillsToLearn', value: string) => {
      if (value.trim()) {
        // Split by comma and add multiple items
        const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
        setLocalInterests(prev => ({
          ...prev,
          [type]: [...(prev[type] || []), ...items]
        }));
        
        if (type === 'interests') setNewInterest('');
        else if (type === 'careerGoals') setNewCareerGoal('');
        else setNewSkill('');
      }
    };

    const removeItem = (type: 'interests' | 'careerGoals' | 'skillsToLearn', index: number) => {
      setLocalInterests(prev => ({
        ...prev,
        [type]: (prev[type] || []).filter((_, i) => i !== index)
      }));
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
          <h2 className="text-2xl font-bold mb-6">Tell us about your interests and goals</h2>
          
          {/* Interests */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Interests & Hobbies</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newInterest}
                onChange={(e) => setNewInterest(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addItem('interests', newInterest)}
                placeholder="Add interests"
                className="flex-1 px-3 py-2 border rounded-md"
              />
              <button
                onClick={() => addItem('interests', newInterest)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {localInterests?.interests?.map((interest, index) => (
                <span key={index} className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  {interest}
                  <button onClick={() => removeItem('interests', index)} className="ml-1 text-indigo-600 hover:text-indigo-800">
                    ×
                  </button>
                </span>
              )) || []}
            </div>
          </div>

          {/* Career Goals */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Career Aspirations</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newCareerGoal}
                onChange={(e) => setNewCareerGoal(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addItem('careerGoals', newCareerGoal)}
                placeholder="Add career goals"
                className="flex-1 px-3 py-2 border rounded-md"
              />
              <button
                onClick={() => addItem('careerGoals', newCareerGoal)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {localInterests?.careerGoals?.map((goal, index) => (
                <span key={index} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  {goal}
                  {jobSalaryData[goal] && (
                    <span className="ml-1 text-xs bg-green-200 px-1 rounded">
                      ${jobSalaryData[goal].toLocaleString()}/yr avg
                    </span>
                  )}
                  <button onClick={() => removeItem('careerGoals', index)} className="ml-1 text-green-600 hover:text-green-800">
                    ×
                  </button>
                </span>
              )) || []}
            </div>
          </div>

          {/* Skills to Learn */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Skills You Want to Learn</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addItem('skillsToLearn', newSkill)}
                placeholder="Add skills"
                className="flex-1 px-3 py-2 border rounded-md"
              />
              <button
                onClick={() => addItem('skillsToLearn', newSkill)}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {localInterests?.skillsToLearn?.map((skill, index) => (
                <span key={index} className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  {skill}
                  <button onClick={() => removeItem('skillsToLearn', index)} className="ml-1 text-purple-600 hover:text-purple-800">
                    ×
                  </button>
                </span>
              )) || []}
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setShowInterestsModal(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={() => saveUserInterests(localInterests)}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Save Interests
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (isStudent && (loading || !dashboardData)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const hasInterests = (userInterests?.interests?.length > 0) || (userInterests?.careerGoals?.length > 0);
  const hasStartedCourse = ((dashboardData?.stats.coursesInProgress || 0) + (dashboardData?.stats.coursesCompleted || 0)) > 0;

  const setupChecklist = [
    {
      key: 'interests',
      label: 'Set your interests',
      done: hasInterests,
      icon: HeartIcon,
      action: () => setShowInterestsModal(true)
    },
    {
      key: 'verify',
      label: 'Verify school or employer',
      done: setupStatus?.verified || false,
      icon: ShieldCheckIcon,
      action: () => document.getElementById('verification-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
    {
      key: 'social',
      label: 'Connect a social profile',
      done: setupStatus?.socialConnected || false,
      icon: LinkIcon,
      action: () => document.getElementById('social-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
    {
      key: 'course',
      label: 'Start your first course',
      done: hasStartedCourse,
      icon: BookOpenIcon,
      action: () => { window.location.href = '/courses'; }
    }
  ];
  const setupDone = setupChecklist.filter(item => item.done).length;

  const StudentDashboard = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 text-white mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">
              Welcome back, {userProfile.firstName}!
            </h1>
            <p className="text-indigo-100 mt-2">
              Continue your learning journey and discover new opportunities
            </p>
          </div>
          <div className="hidden md:block">
            <AcademicCapIcon className="h-16 w-16 text-indigo-200" />
          </div>
        </div>
      </div>

      {/* Profile setup checklist */}
      {setupDone < setupChecklist.length && (
        <div className="bg-white rounded-xl border border-indigo-100 shadow-sm p-5 mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-900">Complete your profile</h2>
            <span className="text-sm text-gray-500">{setupDone} of {setupChecklist.length} done</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mb-4">
            <div
              className="bg-gradient-to-r from-indigo-500 to-purple-500 h-1.5 rounded-full transition-all"
              style={{ width: `${(setupDone / setupChecklist.length) * 100}%` }}
            ></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {setupChecklist.map(item => (
              <button
                key={item.key}
                onClick={item.done ? undefined : item.action}
                disabled={item.done}
                className={`flex items-center gap-2 p-3 rounded-lg border text-left text-sm font-medium transition-colors ${
                  item.done
                    ? 'border-green-100 bg-green-50 text-green-700 cursor-default'
                    : 'border-gray-200 text-gray-700 hover:border-indigo-300 hover:bg-indigo-50'
                }`}
              >
                {item.done
                  ? <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                  : <item.icon className="h-5 w-5 text-indigo-500 flex-shrink-0" />}
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Courses Completed</p>
              <p className="text-2xl font-semibold text-gray-900">{dashboardData?.stats.coursesCompleted || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <BookOpenIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Courses In Progress</p>
              <p className="text-2xl font-semibold text-gray-900">{dashboardData?.stats.coursesInProgress || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <TrophyIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Global Rank</p>
              <p className="text-2xl font-semibold text-gray-900">#{dashboardData?.stats.globalRank || 'N/A'}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100">
              <BriefcaseIcon className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Job Applications</p>
              <p className="text-2xl font-semibold text-gray-900">{dashboardData?.stats.jobApplications || 0}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Learning Section */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  {dashboardData?.hasActiveCourses ? 'Continue Learning' : 'Start Learning'}
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowInterestsModal(true)}
                    className="text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center gap-1"
                  >
                    <CogIcon className="h-4 w-4" />
                    Preferences
                  </button>
                <Link
                  to="/courses"
                  className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                >
                  View all courses
                </Link>
                </div>
              </div>
            </div>
            <div className="p-6">
              {dashboardData?.hasActiveCourses ? (
              <div className="space-y-4">
                  {dashboardData?.recentCourses?.map((enrollment: any, index: number) => (
                  <div key={index} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-gray-900">{enrollment.course?.title || 'Course Title'}</h3>
                        <span className="text-sm text-gray-500">{enrollment.progress || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full"
                          style={{ width: `${enrollment.progress || 0}%` }}
                      ></div>
                    </div>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-600 mr-2">Status:</span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          enrollment.status === 'completed' 
                            ? 'bg-green-100 text-green-800'
                            : enrollment.status === 'in_progress'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {enrollment.status === 'in_progress' ? 'In Progress' : 
                           enrollment.status === 'completed' ? 'Completed' : 
                           enrollment.status || 'In Progress'}
                        </span>
                      </div>
                    <div className="mt-3">
                        <Link
                          to={`/courses/${enrollment.courseId}`}
                          className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                        >
                        Continue Learning →
                        </Link>
                    </div>
                  </div>
                ))}
              </div>
              ) : (
                <div className="text-center py-8">
                  <AcademicCapIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to start learning?</h3>
                  <p className="text-gray-600 mb-6">
                    {(userInterests?.interests?.length > 0) || (userInterests?.careerGoals?.length > 0)
                      ? 'Check out our recommendations based on your interests!'
                      : 'Tell us about your interests to get personalized course recommendations.'
                    }
                  </p>
                  <div className="flex justify-center gap-3">
                    {(userInterests?.interests?.length === 0 || !userInterests?.interests) && (userInterests?.careerGoals?.length === 0 || !userInterests?.careerGoals) && (
                      <button
                        onClick={() => setShowInterestsModal(true)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2"
                      >
                        <HeartIcon className="h-4 w-4" />
                        Set Interests
                      </button>
                    )}
                    <Link
                      to="/courses"
                      className="px-4 py-2 border border-indigo-600 text-indigo-600 rounded-md hover:bg-indigo-50"
                    >
                      Browse All Courses
                    </Link>
                    </div>
                  </div>
              )}
            </div>
          </div>
          <div className="mt-8">
                 <div className="flex items-center justify-between mb-4">
           <div>
             <h2 className="text-xl font-bold text-gray-900">Career Paths</h2>
             <p className="text-gray-600 text-sm">
               {(userInterests?.interests?.length > 0) || (userInterests?.careerGoals?.length > 0)
                 ? 'Recommended paths based on your interests'
                 : 'Popular career paths to explore (add your interests and goals to get personalized recommendations)'
               }
             </p>
           </div>
           {careerPaths.length > 2 && (
             <div className="flex items-center gap-2">
               <button
                 onClick={() => {
                   const container = document.getElementById('career-paths-container');
                   if (container) {
                     container.scrollBy({ left: -400, behavior: 'smooth' });
                   }
                 }}
                 className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
               >
                 <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                 </svg>
               </button>
               <button
                 onClick={() => {
                   const container = document.getElementById('career-paths-container');
                   if (container) {
                     container.scrollBy({ left: 400, behavior: 'smooth' });
                   }
                 }}
                 className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
               >
                 <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                 </svg>
               </button>
             </div>
           )}
         </div>

         <div 
           id="career-paths-container"
           className="flex gap-4 overflow-x-auto scrollbar-hide pb-2"
           style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
         >
          {careerPaths.map((path, index) => (
            <div key={path.id} className="flex-shrink-0 w-80 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden border border-gray-100">
              {/* Header */}
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-3 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="text-2xl">{path.icon}</div>
                    <div>
                      <h3 className="text-lg font-bold">{path.title}</h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          path.difficulty === 'beginner' ? 'bg-green-200 text-green-800' :
                          path.difficulty === 'intermediate' ? 'bg-yellow-200 text-yellow-800' :
                          'bg-red-200 text-red-800'
                        }`}>
                          {path.difficulty}
                        </span>
                        <span className="text-indigo-100 text-xs">{path.duration}</span>
                      </div>
                    </div>
                  </div>
                  {path.matchScore && (
                    <div className="text-right">
                      <div className="text-lg font-bold">{Math.round((path.matchScore / 20) * 100)}%</div>
                      <div className="text-xs text-indigo-200">match</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="p-3">
                <p className="text-gray-600 text-xs mb-3 line-clamp-2">{path.description}</p>

                {/* Market Data */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="text-center p-2 bg-green-50 rounded">
                    <div className="text-sm font-bold text-green-600">
                      {path.realMarketData?.jobCount || 'N/A'}
                    </div>
                    <div className="text-xs text-green-700">Jobs</div>
                  </div>
                  <div className="text-center p-2 bg-blue-50 rounded">
                    <div className="text-sm font-bold text-blue-600">
                      {path.realMarketData?.candidateCount || 'N/A'}
                    </div>
                    <div className="text-xs text-blue-700">Candidates</div>
                  </div>
                  <div className="text-center p-2 bg-purple-50 rounded">
                    <div className="text-sm font-bold text-purple-600">
                      ${Math.round(path.marketData.averageSalary / 1000)}k
                    </div>
                    <div className="text-xs text-purple-700">Avg Salary</div>
                  </div>
                </div>

                                {/* Courses & Skills */}
                <div className="mb-3">
                  <div className="text-xs text-gray-700 mb-1">
                    <span className="font-medium">{path.courses.length} Courses</span> • 
                    <span className="font-medium"> {path.skills.length} Skills</span>
                    {path.realMarketData?.competitionLevel && (
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                        path.realMarketData.competitionLevel === 'low' ? 'bg-green-100 text-green-700' :
                        path.realMarketData.competitionLevel === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {path.realMarketData.competitionLevel} comp
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {path.skills.slice(0, 3).map((skill: string, skillIndex: number) => (
                      <span key={skillIndex} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {skill}
                      </span>
                    ))}
                    {path.skills.length > 3 && (
                      <span className="text-xs text-gray-500">
                        +{path.skills.length - 3}
                      </span>
                    )}
              </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => enrollInCareerPath(path.id)}
                  disabled={enrollingPath === path.id}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2 px-3 rounded font-medium hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-xs"
                >
                  {enrollingPath === path.id ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                      Enrolling...
                    </>
                  ) : (
                    `Start Path`
                  )}
                </button>
                             </div>
             </div>
           ))}
            </div>
          </div>
        </div>

        {/* Quick Actions & Recent Jobs */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <Link
                  to="/courses"
                  className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <BookOpenIcon className="h-5 w-5 text-indigo-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">Browse Courses</span>
                </Link>
                <Link
                  to="/jobs"
                  className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <BriefcaseIcon className="h-5 w-5 text-indigo-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">Find Jobs</span>
                </Link>
                <Link
                  to="/precise-application-path"
                  className="flex items-center p-3 rounded-lg hover:bg-indigo-50 transition-colors"
                >
                  <SparklesIcon className="h-5 w-5 text-indigo-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">Precise Application Path</span>
                  <span className="ml-auto px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-700">Premium</span>
                </Link>
                 {/*  <Link
                  to="/rankings"
                  className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <TrophyIcon className="h-5 w-5 text-indigo-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">View Rankings</span>
                </Link>   */} 
                <Link
                  to="/profile"
                  className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <UserGroupIcon className="h-5 w-5 text-indigo-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">Edit Profile</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Verification (school / employer) */}
          <div id="verification-card">
            <VerificationsCard />
          </div>

          {/* Social connections */}
          <div id="social-card">
            <SocialConnectionsCard />
          </div>

          {/* Course Recommendations & Career Goals */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  {(userInterests?.interests?.length > 0) || (userInterests?.careerGoals?.length > 0)
                    ? 'Recommended for You' 
                    : 'Popular Courses'
                  }
                </h2>
                <Link
                  to="/courses"
                  className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                >
                  View all
                </Link>
              </div>
            </div>
            <div className="p-6">
              {(userInterests?.careerGoals?.length > 0) && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Your Career Goals</h3>
                  <div className="space-y-2">
                    {userInterests?.careerGoals?.slice(0, 3).map((goal, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                        <span className="text-sm text-green-800">{goal}</span>
                        {jobSalaryData[goal] && (
                          <div className="flex items-center gap-1 text-xs text-green-600">
                            <CurrencyDollarIcon className="h-3 w-3" />
                            {jobSalaryData[goal].toLocaleString()}/yr avg
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="space-y-4">
                {recommendedCourses?.length > 0 ? (
                  recommendedCourses?.slice(0, 3).map((course, index) => (
                    <div key={course.id || index} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-gray-900">{course.title}</h4>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          course.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                          course.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {course.difficulty}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{course.category}</p>
                      {course.marketData && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            ${course.marketData.averageSalary?.toLocaleString()}/yr
                          </span>
                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                            {course.marketData.jobDemand}% demand
                          </span>
                        </div>
                      )}
                      <Link
                        to={`/courses/${course.id}`}
                        className="mt-2 inline-block text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                      >
                        View Course →
                      </Link>
                    </div>
                  ))
                ) : (
                  [
                    { title: 'Introduction to Programming', category: 'Computer Science', difficulty: 'beginner' },
                    { title: 'Data Analysis Basics', category: 'Data Science', difficulty: 'beginner' },
                    { title: 'Digital Marketing Fundamentals', category: 'Marketing', difficulty: 'beginner' }
                  ].map((course, index) => (
                  <div key={index} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-gray-900">{course.title}</h4>
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          {course.difficulty}
                      </span>
                    </div>
                      <p className="text-sm text-gray-600">{course.category}</p>
                      <Link
                        to="/courses"
                        className="mt-2 inline-block text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                      >
                        View Course →
                      </Link>
                  </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  interface EmployerDashboardStats {
    activeJobs: number;
    totalApplications: number;
    profileViews: number;
    shortlisted: number;
  }

  interface RecentApplication {
    id: string;
    name: string;
    position: string;
    skills: string[];
    match: number;
    applied: string;
    jobId: string;
    studentId: string;
  }

  interface TopPerformingJob {
    id: string;
    title: string;
    applications: number;
    views: number;
  }

  interface EmployerDashboardData {
    stats: EmployerDashboardStats;
    recentApplications: RecentApplication[];
    topPerformingJobs: TopPerformingJob[];
  }

  const EmployerDashboard = () => {
    const [dashboardData, setDashboardData] = useState<EmployerDashboardData | null>(null);
    const [loading, setLoading] = useState(true);

    // Helper function to get proper applicant name
    const getApplicantName = (application: RecentApplication): string => {
      // First try the name from the application
      if (application.name && application.name.trim() !== '' && application.name !== 'Unknown' && application.name !== 'Unknown Student') {
        return application.name;
      }

      // If name is missing or unknown, use studentId as fallback
      if (application.studentId) {
        // Try to extract name from studentId or create a placeholder
        return `Student ${application.studentId.slice(-4)}`;
      }

      // Final fallback
      return 'Unknown Applicant';
    };

    const fetchEmployerDashboardData = async () => {
      console.log('EmployerDashboard: Starting data fetch');
      try {
        setLoading(true);
        
        const token = localStorage.getItem('authToken');
        console.log('EmployerDashboard: Making API request to /jobs/employer/dashboard');
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/employer/dashboard`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });

        console.log('EmployerDashboard: Received response, status:', response.status);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('EmployerDashboard: API response:', result);
        
        if (result.success) {
          setDashboardData(result.data);
        } else {
          throw new Error(result.message || 'Failed to fetch dashboard data');
        }
        console.log('EmployerDashboard: Data fetch completed successfully');
      } catch (error: any) {
        console.error('EmployerDashboard: Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data');
        
        // Fallback to empty data
        setDashboardData({
          stats: {
            activeJobs: 0,
            totalApplications: 0,
            profileViews: 0,
            shortlisted: 0
          },
          recentApplications: [],
          topPerformingJobs: []
        });
      } finally {
        console.log('EmployerDashboard: Setting loading to false');
        setLoading(false);
      }
    };

    useEffect(() => {
      console.log('EmployerDashboard: useEffect triggered, calling fetchEmployerDashboardData');
      fetchEmployerDashboardData();
    }, []);

    if (loading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-8 text-white mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">
              Welcome back, {userProfile.firstName}!
            </h1>
            <p className="text-blue-100 mt-2">
              Manage your job postings and find the perfect candidates
            </p>
          </div>
          <div className="hidden md:block">
            <BriefcaseIcon className="h-16 w-16 text-blue-200" />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <DocumentTextIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Jobs</p>
              <p className="text-2xl font-semibold text-gray-900">{dashboardData?.stats.activeJobs || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <UsersIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Applications</p>
              <p className="text-2xl font-semibold text-gray-900">{dashboardData?.stats.totalApplications || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <EyeIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Profile Views</p>
              <p className="text-2xl font-semibold text-gray-900">{dashboardData?.stats.profileViews.toLocaleString() || '0'}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100">
              <StarIcon className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Shortlisted</p>
              <p className="text-2xl font-semibold text-gray-900">{dashboardData?.stats.shortlisted || 0}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Applications */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Recent Applications</h2>
                <Link
                  to="/applications"
                  className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                >
                  View all applications
                </Link>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {dashboardData?.recentApplications && dashboardData.recentApplications.length > 0 ? (
                  dashboardData.recentApplications.map((application, index) => (
                  <div key={index} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="font-medium text-gray-900">{getApplicantName(application)}</h3>
                        <p className="text-sm text-gray-600">Applied for {application.position}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          {application.match}% match
                        </span>
                        <p className="text-xs text-gray-500 mt-1">{application.applied}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {application.skills?.map((skill, skillIndex) => (
                        <span key={skillIndex} className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
                          {skill}
                        </span>
                      ))}
                    </div>
                    <div className="flex space-x-2">
                      <button className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">
                        View Profile
                      </button>
                      <button className="text-green-600 hover:text-green-700 text-sm font-medium">
                        Shortlist
                      </button>
                      <button className="text-red-600 hover:text-red-700 text-sm font-medium">
                        Reject
                      </button>
                    </div>
                  </div>
                ))
                ) : (
                  <div className="text-center py-8">
                    <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No recent applications</p>
                    <p className="text-sm text-gray-400">Applications will appear here once candidates start applying to your jobs</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions & Job Performance */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <Link
                  to="/post-job"
                  className="flex items-center p-3 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors"
                >
                  <PlusIcon className="h-5 w-5 text-indigo-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">Post New Job</span>
                </Link>
                <Link
                  to="/employer/candidates"
                  className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <UsersIcon className="h-5 w-5 text-indigo-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">Browse Candidates</span>
                </Link>
                <Link
                  to="/employer/analytics"
                  className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <ChartBarIcon className="h-5 w-5 text-indigo-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">View Analytics</span>
                </Link>
                <Link
                  to="/courses/create"
                  className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <BookOpenIcon className="h-5 w-5 text-indigo-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">Create Assessment</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Company verification */}
          <VerificationsCard kinds={['employment']} />

          {/* Top Performing Jobs */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Top Performing Jobs</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {dashboardData?.topPerformingJobs && dashboardData.topPerformingJobs.length > 0 ? (
                  dashboardData.topPerformingJobs.map((job, index) => (
                    <div key={job.id} className="border rounded-lg p-3">
                      <h4 className="font-medium text-gray-900 mb-2">{job.title}</h4>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>{job.applications} applications</span>
                        <span>{job.views} views</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No job performance data</p>
                    <p className="text-sm text-gray-400">Post some jobs to see performance metrics here</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {isStudent ? <StudentDashboard /> : <EmployerDashboard />}
      {showInterestsModal && <InterestsModal />}
    </div>
  );
};

export default Dashboard; 